import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components/macro';
import GTM from '@mirinae/apis/GoogleAnalytics';

import { os } from '@mirinae/js-utils/utils/userAgent';
import { palette, fontDefaultAlt } from '@mirinae/react-ui';
import { UserContext } from '@mirinae/react-auth';

import IconButtonArrow from '@mirinae/lessons/components/lesson/icons/IconButtonArrow';
import LessonCard from '@mirinae/lessons/components/card/LessonCard';
// import LessonCardPlaceholder from '@mirinae/lessons/components/card/LessonCardPlaceholder';
import { lessonsBreakPoint, lessonsPalette } from '@mirinae/lessons/modules/defines/styles';
import {
    apiPaths, authPaths, navigateExternal
} from '@mirinae/shared/defines/paths';
import ModalPopup from '@mirinae/lessons/components/modal/ModalPopup';
import { LessonButton } from '@mirinae/lessons/modules/defines/styles';
import { getUserType } from '@mirinae/shared/defines/datas';
import UpgradeProPopup from '@mirinae/lessons/components/modal/UpgradeProPopup';
import httpAPI from '@mirinae/lessons/modules/apis/http';
import { useProAccessStoreMethods } from '@mirinae/stores/proAccessStore';

export const LessonTitle = styled.div`
    position: relative;
    font-size: 18px;
    font-weight: 700;
    color: ${palette.green.middle};

    ${os.isOthers ? `
        margin-top: 40px;
        margin-bottom: 20px;
    ` : `
        margin-top: 45px;
        margin-bottom: 15px;
    `}
    span {
        color: ${palette.point.black};
    }
`;
export const LessonTitleMoreButton = styled(Link)`
    position: absolute;
    right: 10px;
    top: 7px;
    display: block;
    height: 18px;
    color: ${palette.green.middle};
    font-size: 14px;
    font-weight: 700;
    font-family: ${fontDefaultAlt};
    svg {
        vertical-align: middle;
    }
`;

const LessonCardRow = styled.div`
    display: flex;
    position: relative;
    ${props => (props.theme === 'main' && props.ua === 'mobile' ? ` // disable for now
        overflow-x: auto;
        width: ${window.innerWidth}px;
    ` : `
        flex-wrap: wrap;
    `)}
    ${props => (props.ua === 'desktop' ? `
        margin: 0 -10px -20px;
    ` : `
        display: flex;
        margin: 0 -20px;
        padding: 5px 15px 0;
    `)}
`;
const LessonCardNav = styled.div`
    display: flex;
    justify-content: space-between;
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    padding: 0 10px 20px;
    transform: translateY(-50%);
    pointer-events: none;

    button {
        transition: opacity 0.3s ease-in-out;
        opacity: ${props => (props.state ? '1' : '0')};
    }
`;
const LessonCardNavButton = styled(LessonButton)`
    pointer-events: auto;
    width: 30px;
    height: 55px;
    border-radius: ${props => (props.theme === 'prev' ? '0 5px 5px 0' : '5px 0 0 5px')};
    background-color: ${lessonsPalette.grey[3]};
    &:hover:not(:disabled) {
        background-color: ${palette.point.black};
    }
    &:disabled {
        background-color: rgba(223, 223, 223, .6);
    }
    ${props => props.theme === 'prev' && (`
        svg {
            transform: rotate(180deg);
        }
    `)}
`;
const EmptyLessonCard = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 270px;
`;

const LessonCardList = ({ lessons, label, theme }) => {
    const [hover, setHover] = useState(false);
    const [slideCount, setSlideCount] = useState(0);
    const [slideData, setSlideData] = useState([]);
    const [rowSize, setRowSize] = useState(0);
    const [displayModal, setDisplayModal] = useState('');
    const [viewCounts, setViewCounts] = useState({});
    const [userContext] = useContext(UserContext);
    const user = userContext.user;
    const { clearAccessKeys } = useProAccessStoreMethods();

    const userType = getUserType(user);
    const isGuest = userType === 'guest';
    const isEditor = user && user.role && user.role.match(/admin|super|lesson/i);

    const handleClick = (value) => {
        let cnt;
        if (value < 0 && slideCount === 0) {
            cnt = Math.floor(lessons.length / rowSize);
        } else if (value > 0 && slideCount === Math.floor(lessons.length / rowSize)) {
            cnt = 0;
        } else {
            cnt = slideCount + value;
        }
        setSlideCount(cnt);
        setSlideData(os.isOthers ? lessons.slice(cnt * rowSize, cnt * rowSize + rowSize) : lessons);
    };
    const handleSetSize = () => {
        if (os.isOthers) {
            if (window.innerWidth <= parseInt(lessonsBreakPoint.sm, 10)) {
                setRowSize(3);
            } else if (window.innerWidth <= parseInt(lessonsBreakPoint.md, 10)) {
                setRowSize(4);
            } else {
                setRowSize(5);
            }
        }
    };

    useEffect(() => {
        setSlideCount(0);
        setSlideData(os.isOthers ? (theme === 'main' ? lessons.slice(0, rowSize) : lessons) : lessons);
    }, [lessons, rowSize]);

    useEffect(() => {
        handleSetSize();
        window.addEventListener('resize', handleSetSize, false);

        return () => {
            window.removeEventListener('resize', handleSetSize);
            clearAccessKeys();
        }
    }, []);

    useLayoutEffect(() => {
        if (isEditor) {
            httpAPI('', apiPaths.viewCounts, { method: 'GET' })
                .then(response => {
                    const { counts } = response.data;
                    setViewCounts(counts.reduce((table, lesson) => { table[lesson._id] = lesson.views[0].count; return table; }, {}));
                });
        }
    }, []);

    return (
        <>
            <LessonCardRow theme={theme} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} ua={os.isOthers ? 'desktop' : 'mobile'}>
                { slideData.length === 0 ? (
                    <EmptyLessonCard>
                        You don’t have any {label} lessons
                    </EmptyLessonCard>
                ) : slideData.map(lesson => (
                    <LessonCard
                        user={user}
                        lessons-t={`lesson-card-${lesson.title}`}
                        key={lesson._id}
                        data={{
                            levelTitle: lesson.lessonNode.levelTitle,
                            listingLabel: lesson.lessonNode.listingLabel || lesson.lessonNode.subtitle,
                            // link: lessonLink(lesson),
                            title: lesson.lessonNode.startPageTitle || lesson.title,
                            sections: lesson.lessonNode.sections,
                            lessonObject: lesson,
                            set: lesson.set,
                        }}
                        setDisplayModal={setDisplayModal}
                        progress={lesson.progress}
                        state={lesson.lessonTracker ? lesson.lessonTracker.state : 'start'}
                        theme={theme}
                        type={lesson.lessonNode.status === 'Coming soon' ? 'wait' : (lesson.lessonNode.guestAccess || userType === 'pro' || isEditor ? 'normal' : 'isLocked')}
                        number={lesson.lessonNode.number}
                        viewCount={viewCounts[lesson._id]}
                    />
                ))}

                { os.isOthers && theme === 'main' && lessons.length > rowSize
                && (
                    <LessonCardNav state={hover}>
                        <LessonCardNavButton type="button" theme="prev" onClick={() => handleClick(-1)}>
                            <IconButtonArrow />
                        </LessonCardNavButton>
                        <LessonCardNavButton type="button" theme="next" onClick={() => handleClick(1)}>
                            <IconButtonArrow />
                        </LessonCardNavButton>
                    </LessonCardNav>
                )}
            </LessonCardRow>

            { displayModal === 'proLocked' && (
                <UpgradeProPopup onClose={() => setDisplayModal('')} />
            )}
            { displayModal === 'wait' && (
                <ModalPopup
                    theme="wait"
                    title="We’re working on it..."
                    message="New lessons are released regularly, so please check back again soon!"
                    textAgree="Okay"
                    onAgree={() => setDisplayModal('')}
                />
            )}
            { displayModal === 'guestLocked' && (
                <ModalPopup
                    theme="guestLocked"
                    title="This lesson is locked for guest users"
                    message="Locked lessons can be unlocked by logging in or signing up for a free Mirinae account."
                    textAgree="Log in"
                    onAgree={() => {
                        if (window.ProfileChannel) {
                            window.ProfileChannel.postMessage(JSON.stringify({ command: 'signOut', value: true })); // webview
                        } else if (window.flutter_inappwebview) {
                            window.flutter_inappwebview.callHandler('ProfileChannel', { command: 'signOut', value: true });
                        } else {
                            const loginURL =  `${authPaths.login}/?redirect=/lessons`;
                            navigateExternal(loginURL, { replace: true });
                        }
                        setDisplayModal('');
                    }}
                    textDeny="No thanks"
                    onDeny={() => setDisplayModal('')}
                />
            )}
        </>
    );
};

export default LessonCardList;